import { useState, useEffect, useMemo } from "react";
import api, { fmtTime, apiError } from "@/lib/api";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import { Modal, Field, Select, Row } from "@/pages/Notizie";
import { EntitySearch } from "@/components/EntitySearch";
import { TipoPicker } from "@/components/TipoPicker";
import { useAuth } from "@/context/AuthContext";

const rbac_isSocio = (u) => u?.role === "direzione" || (u?.roles || []).includes("direzione");
import { toast } from "sonner";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { TIPO_APPT_OPTS } from "@/lib/agendaTipi";
import { EsitoFields, apptNeedsEsito } from "@/components/EsitoModal";
import { conRuolo } from "@/lib/ruoli";
import { PersonaSearch } from "@/components/PersonaSearch";

export const TIPO_OPTS = TIPO_APPT_OPTS;

const toWall = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
};
const STATO_OPTS = [["programmato", "Programmato"], ["annullato", "Annullato"]];
const DURATA_OPTS = [["30", "30 min"], ["45", "45 min"], ["60", "1 ora"], ["90", "1 ora e 30"], ["120", "2 ore"]];
const DOW = [["0", "Lun"], ["1", "Mar"], ["2", "Mer"], ["3", "Gio"], ["4", "Ven"], ["5", "Sab"], ["6", "Dom"]];

function propFromEvent(e) {
  if (!e) return null;
  if (e.immobile_id) return { type: "immobile", id: e.immobile_id, label: e.immobile_titolo || "Immobile" };
  if (e.notizia_id) return { type: "notizia", id: e.notizia_id, label: e.immobile_titolo || "Notizia" };
  return null;
}
function cliFromEvent(e) {
  if (!e || !e.cliente_id) return null;
  return { type: e.cliente_tipo || "proprietario", id: e.cliente_id, label: e.cliente_nome || "Cliente" };
}

export function AppuntamentoModal({ event, prefill = {}, onClose, onSaved, onDeleted }) {
  const isEdit = !!event;
  const initData = event?.data ? new Date(event.data) : null;
  const initStr = initData ? format(initData, "yyyy-MM-dd'T'HH:mm") : (prefill.date || "");
  const [f, setF] = useState({
    titolo: event?.titolo || prefill.titolo || "",
    tipo: event?.tipo || prefill.tipo || "",
    stato: event?.stato === "annullato" ? "annullato" : "programmato",
    data: initStr,
    durata_min: String(event?.durata_min || prefill.durata_min || 60),
  });
  const [prop, setProp] = useState(propFromEvent(event) || prefill.property || null);
  const [cli, setCli] = useState(cliFromEvent(event) || prefill.client || null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [confirmConflict, setConfirmConflict] = useState(false);
  const { errors, clear, validate, applyServer, summary } = useFormErrors();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); setConflicts(null); setConfirmConflict(false); };

  // Block (Agenda bloccata) is only creatable from the "new" flow; editing targets a real appointment.
  const isBlock = !isEdit && f.tipo === "bloccato";
  const [bmode, setBmode] = useState("once");
  const [bweekdays, setBweekdays] = useState([]);
  const [bstartT, setBstartT] = useState("09:00");
  const [bendT, setBendT] = useState("11:00");
  const [buntil, setBuntil] = useState("");
  const toggleWd = (d) => setBweekdays((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);

  const agentId = prefill.agentId || event?.owner_agent_id;

  const [assignees, setAssignees] = useState([]);
  const [owner, setOwner] = useState(event?.owner_agent_id || prefill.agentId || "");
  const [participants, setParticipants] = useState(event?.partecipanti_ids || []);
  useEffect(() => { api.get("/appuntamenti/assignees").then((r) => setAssignees(r.data || [])).catch(() => setAssignees([])); }, []);
  const assIds = new Set(assignees.map((a) => a.id));
  const fissi = (event?.partecipanti_ids || []).map((id, i) => ({ id, name: (event?.partecipanti_nomi || [])[i] || "Partecipante" })).filter((p) => !assIds.has(p.id) && p.id !== owner);
  const titolari = assignees.filter((a) => a.titolare !== false);
  const ownerExtra = event?.owner_agent_id && !titolari.some((a) => a.id === event.owner_agent_id) ? [[event.owner_agent_id, assignees.find((a) => a.id === event.owner_agent_id)?.name || "Titolare attuale"]] : [];

  const { user } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [outsideUser, setOutsideUser] = useState("");
  useEffect(() => { if (!isEdit) api.get("/agenda/richieste/users").then((r) => setAllUsers(r.data || [])).catch(() => setAllUsers([])); }, [isEdit]);
  const circleIds = useMemo(() => new Set([...(assignees || []).map((a) => a.id), user?.id]), [assignees, user]);
  const outsideUsers = useMemo(() => (allUsers || []).filter((u) => !circleIds.has(u.id)), [allUsers, circleIds]);
  const requestMode = !isBlock && !!outsideUser;

  const setTipo = (v) => {
    setF((s) => ({ ...s, tipo: v }));
    clear("tipo"); setConflicts(null); setConfirmConflict(false);
    if (v === "bloccato") setOutsideUser("");
  };

  const buildPayload = (startISO) => {
    const p = {
      titolo: f.titolo, tipo: f.tipo, stato: f.stato, data: startISO,
      durata_min: parseInt(f.durata_min) || 60,
      immobile_id: prop && prop.type === "immobile" ? prop.id : null,
      notizia_id: prop && prop.type === "notizia" ? prop.id : null,
      immobile_titolo: prop ? prop.label : "",
      cliente_id: cli ? cli.id : null,
      cliente_tipo: cli ? cli.type : null,
      cliente_nome: cli ? cli.label : "",
      partecipanti_ids: participants.filter((x) => x !== owner),
      da_collegare: false,
    };
    if (owner) p.owner_agent_id = owner;
    else if (prefill.agentId) p.owner_agent_id = prefill.agentId;
    return p;
  };

  const saveBlock = async () => {
    if (bmode === "weekly" && bweekdays.length === 0) return toast.error("Seleziona almeno un giorno");
    setSaving(true);
    try {
      const payload = bmode === "weekly"
        ? { kind: "weekly", titolo: f.titolo, weekdays: bweekdays.map(Number), start_time: bstartT, end_time: bendT, until: buntil || null }
        : { kind: "once", titolo: f.titolo, data: new Date(f.data).toISOString(), durata_min: parseInt(f.durata_min) || 60 };
      await api.post("/agenda/blocchi", payload);
      toast.success("Agenda bloccata creata");
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const save = async () => {
    const rules = { tipo: [V.required] };
    if (isBlock) { if (bmode === "once") rules.data = [V.required]; }
    else { rules.titolo = [V.required]; rules.data = [V.required]; }
    const errs = validate(rules, f);
    if (Object.keys(errs).length) return toast.error(errSummary(Object.keys(errs).length));
    if (isBlock) return saveBlock();
    if (requestMode) {
      setSaving(true);
      try {
        await api.post("/agenda/richieste", {
          to_user_id: outsideUser, data: toWall(new Date(f.data)), durata_min: parseInt(f.durata_min) || 60,
          tipo: f.tipo,
          motivo: f.titolo,
          immobile_id: prop && prop.type === "immobile" ? prop.id : null, immobile_titolo: prop ? prop.label : "",
          cliente_id: cli ? cli.id : null, cliente_nome: cli ? cli.label : "",
        });
        toast.success("Richiesta di appuntamento inviata");
        onSaved && onSaved();
      } catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
      return;
    }
    const startISO = toWall(new Date(f.data));
    const endISO = toWall(new Date(new Date(f.data).getTime() + (parseInt(f.durata_min) || 60) * 60000));
    if (!confirmConflict) {
      try {
        const { data } = await api.get("/appuntamenti/conflicts", { params: { start: startISO, end: endISO, exclude: event?.id, agent_id: agentId } });
        if (data.has_conflict) { setConflicts(data.conflicts); setConfirmConflict(true); return; }
      } catch { /* non-blocking */ }
    }
    setSaving(true);
    try {
      const payload = buildPayload(startISO);
      if (isEdit) { await api.put(`/appuntamenti/${event.id}`, payload); toast.success("Appuntamento aggiornato"); }
      else { await api.post("/appuntamenti", payload); toast.success("Appuntamento creato"); }
      onSaved && onSaved();
    } catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (rbac_isSocio(user) && !window.confirm("Sei sicura? L'appuntamento verrà eliminato e i partecipanti avvisati.")) return;
    setDeleting(true);
    try { await api.delete(`/appuntamenti/${event.id}`); toast.success("Appuntamento eliminato"); (onDeleted || onSaved)(); }
    catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const title = requestMode ? "Richiesta di appuntamento" : isBlock ? "Agenda bloccata" : (isEdit ? "Modifica appuntamento" : "Nuovo appuntamento");
  const saveLabel = isBlock ? "Crea blocco" : requestMode ? "Invia richiesta" : (confirmConflict ? "Conferma comunque" : "Salva");

  return (
    <Modal title={title} onClose={onClose} onSave={save} saving={saving} saveLabel={saveLabel} testid="appuntamento-modal" summary={summary} scheda value={{ f, prop, cli }} onRestore={(v) => { setF(v.f); setProp(v.prop); setCli(v.cli); }}>
      {isEdit && apptNeedsEsito(event) && (
        <div className="rounded-xl border-2 border-[#FC82ED] bg-[#FC82ED]/10 p-4" data-testid="esito-inline">
          <h3 className="font-display text-base font-bold mb-0.5">Com'è andata?</h3>
          <p className="text-xs text-[var(--muted)] mb-3">Questo appuntamento è finito: registra l'esito.</p>
          <EsitoFields appt={event} onSaved={() => onSaved && onSaved()} />
        </div>
      )}
      <TipoPicker value={f.tipo} onChange={setTipo} error={errors.tipo} includeBlocked={!isEdit} />

      {conflicts && conflicts.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="conflict-warning">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 mb-1.5"><AlertTriangle size={16} /> Conflitto di orario</div>
          <ul className="text-xs text-amber-700 space-y-0.5">
            {conflicts.map((c, i) => <li key={i}>• {fmtTime(c.start)}–{fmtTime(c.end)} · {c.label}</li>)}
          </ul>
          <p className="text-[11px] text-amber-600 mt-1.5">Puoi comunque confermare l'appuntamento.</p>
        </div>
      )}

      {isBlock ? (
        <>
          <p className="text-xs text-[var(--muted)]">Impegno personale o tra soci. Gli altri vedranno solo "Non disponibile": in questo orario nessuno potrà fissarti appuntamenti o inviarti richieste.</p>
          <Field label="Titolo (visibile solo a te)" testid="ap-titolo" value={f.titolo} onChange={set("titolo")} />
          <div className="flex gap-1 bg-[#F7F7F8] border border-[var(--border)] rounded-xl p-1 w-max">
            {[["once", "Una tantum"], ["weekly", "Settimanale"]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`blocco-mode-${k}`} onClick={() => setBmode(k)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${bmode === k ? "bg-[#111827] text-white" : "text-slate-600"}`}>{l}</button>
            ))}
          </div>
          {bmode === "once" ? (
            <Row>
              <Field label="Data e ora" type="datetime-local" required error={errors.data} testid="ap-data" value={f.data} onChange={set("data")} />
              <Select label="Durata" value={f.durata_min} testid="ap-durata" onChange={set("durata_min")} opts={DURATA_OPTS} />
            </Row>
          ) : (
            <>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Giorni della settimana</label>
                <div className="flex gap-1.5 flex-wrap">
                  {DOW.map(([d, l]) => (
                    <button key={d} type="button" data-testid={`blocco-wd-${d}`} onClick={() => toggleWd(d)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${bweekdays.includes(d) ? "bg-[#111827] text-white border-[#111827]" : "border-[var(--border)] text-slate-500"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <Row>
                <Field label="Dalle" type="time" testid="blocco-start" value={bstartT} onChange={(e) => setBstartT(e.target.value)} />
                <Field label="Alle" type="time" testid="blocco-end" value={bendT} onChange={(e) => setBendT(e.target.value)} />
              </Row>
              <Field label="Fino al (opzionale)" type="date" testid="blocco-until" value={buntil} onChange={(e) => setBuntil(e.target.value)} />
            </>
          )}
        </>
      ) : (
        <>
          <Field label="Titolo" testid="ap-titolo" required value={f.titolo} error={errors.titolo} onChange={set("titolo")} />
          <Row>
            <Field label="Data e ora" type="datetime-local" required error={errors.data} value={f.data} testid="ap-data" onChange={set("data")} />
            <Select label="Durata" value={f.durata_min} testid="ap-durata" onChange={set("durata_min")} opts={DURATA_OPTS} />
          </Row>
          {isEdit && <Select label="Stato" value={f.stato} testid="ap-stato" onChange={set("stato")} opts={STATO_OPTS} />}
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Immobile / Notizia collegata</label>
            <EntitySearch testid="ap-immobile-search" kinds={["immobile", "notizia"]} value={prop} onSelect={setProp} placeholder="Cerca per titolo o indirizzo…" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Cliente collegato</label>
            <EntitySearch testid="ap-cliente-search" kinds={["proprietario", "acquirente"]} value={cli} onSelect={setCli} placeholder="Cerca per nome o telefono…" />
          </div>
          {assignees.length > 1 && (
            <>
              <Select label="Titolare (agente assegnato)" value={owner} testid="ap-owner"
                      onChange={(e) => { const v = e.target.value; setOwner(v); setParticipants((s) => s.filter((x) => x !== v)); }}
                      opts={[["", "Io (predefinito)"], ...ownerExtra, ...titolari.map((a) => [a.id, conRuolo(a)])]} />
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Altri partecipanti</label>
                <div data-testid="ap-participants">
                  <PersonaSearch testid="ap-part-search" people={assignees.filter((a) => a.id !== owner)} sel={participants.filter((x) => assignees.some((a) => a.id === x))} onChange={(v) => setParticipants([...participants.filter((x) => !assignees.some((a) => a.id === x)), ...v])} />
                </div>
                <p className="text-[11px] text-[var(--muted)] mt-1">L'appuntamento comparirà nell'agenda di tutti i partecipanti e ognuno riceverà le notifiche.</p>
              </div>
            </>
          )}
          {fissi.length > 0 && (
            <div data-testid="ap-partecipanti-fissi">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Altri partecipanti già presenti</label>
              <div className="flex flex-wrap gap-1.5">{fissi.map((p) => <span key={p.id} data-testid={`ap-part-fisso-${p.id}`} title="Fuori dal tuo elenco: non rimovibile" className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{p.name}</span>)}</div>
            </div>
          )}
          {!isEdit && outsideUsers.length > 0 && (
            <div>
              <Select label="Invita una persona fuori dalle tue cerchie" value={outsideUser} testid="ap-outside"
                      onChange={(e) => setOutsideUser(e.target.value)}
                      opts={[["", "— nessuno —"], ...outsideUsers.map((u) => [u.id, `${u.name}${u.team_name ? ` · ${u.team_name}` : ""}`])]} />
              {requestMode && (
                <div data-testid="ap-request-banner" className="mt-2 rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-700">
                  Questa persona è fuori dalle tue cerchie: verrà inviata una <b>Richiesta di appuntamento</b>. Potrà accettarla, rifiutarla o proporre un altro orario. Il titolare e gli altri partecipanti vengono ignorati.
                </div>
              )}
            </div>
          )}
        </>
      )}

      {isEdit && (
        <button type="button" data-testid="ap-delete-btn" onClick={remove} disabled={deleting}
                className="w-full mt-1 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-100 flex items-center justify-center gap-2 disabled:opacity-60">
          {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Elimina appuntamento
        </button>
      )}
    </Modal>
  );
}
